/**
 * Parent Zone session — Phase 5.2.
 *
 * Local storage for the 15min HMAC session token returned by
 * POST /v1/parent/auth. Two storage backends:
 *
 *  - Mobile: expo-secure-store (Keychain on iOS, Keystore on Android)
 *  - Web: localStorage (PIN gate is soft on web since the device is
 *    typically shared with a child less; Y1 doesn't require
 *    SecureStore fallback on web)
 *
 * The session token is also used by apiFetch to attach the
 * X-Parent-Session header on every Parent Zone call.
 */

import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

const KEY = 'funpaper.parent_session.v1';

export interface StoredSession {
  token: string;
  expiresAt: string; // ISO 8601
  deviceId: string;
}

export async function saveSession(session: StoredSession): Promise<void> {
  if (Platform.OS === 'web') {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(KEY, JSON.stringify(session));
    } catch {
      // PIN verification succeeded on the server; keep the failure local and
      // let the caller show its normal retry path instead of throwing.
    }
    return;
  }
  await SecureStore.setItemAsync(KEY, JSON.stringify(session));
}

export async function loadSession(): Promise<StoredSession | null> {
  let raw: string | null = null;
  if (Platform.OS === 'web') {
    if (typeof window === 'undefined') return null;
    raw = window.localStorage.getItem(KEY);
  } else {
    raw = await SecureStore.getItemAsync(KEY);
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredSession;
    if (typeof parsed.token !== 'string') return null;
    if (typeof parsed.expiresAt !== 'string') return null;
    // Reject if expired.
    if (Date.parse(parsed.expiresAt) < Date.now()) {
      await clearSession();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export async function clearSession(): Promise<void> {
  if (Platform.OS === 'web') {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(KEY);
    } catch {
      // Ignore unavailable localStorage during logout/expiry cleanup.
    }
    return;
  }
  await SecureStore.deleteItemAsync(KEY);
}

/** True if the stored session is still valid (not expired). */
export async function isSessionValid(): Promise<boolean> {
  const session = await loadSession();
  return session !== null;
}
